import { errorDialogDefaultFunction } from '@bludata/utility-functions';
import axios from 'axios';
import { NavigateFunction } from 'react-router';

/**
 * Naviga l'utente a una pagina specifica del cliente o aggiorna i parametri
 * di ricerca della pagina corrente con l'ID del cliente.
 *
 * Questa funzione determina l'ID cliente da utilizzare per la navigazione. Se `idCliente`
 * è fornito, viene utilizzato direttamente. Se viene fornito solo `codicePersonale`,
 * tenta di recuperare l'`idCliente` corrispondente dall'API. Se nessuno dei due
 * viene fornito, o se la ricerca API fallisce, la funzione termina
 * anticipatamente senza navigare.
 *
 * Se viene specificato un `path`, naviga a quel percorso, aggiungendo l'ID cliente
 * come parametro di ricerca (`?idCliente=...`). Se non viene specificato alcun `path`,
 * aggiorna i parametri di ricerca del percorso corrente con l'ID cliente.
 *
 * @param navigate - L'istanza `NavigateFunction` da `react-router-dom` utilizzata
 *   per eseguire la navigazione.
 * @param params - Un oggetto contenente i parametri per la navigazione.
 * @param params.path - Opzionale. Il pathname di destinazione per la navigazione (es. '/clienti/details').
 *   Se omesso, la navigazione aggiornerà solo i parametri di ricerca del percorso corrente.
 * @param params.idCliente - Opzionale. L'ID diretto del cliente a cui navigare.
 *   Ha la precedenza su `codicePersonale` se entrambi sono forniti.
 * @param params.codicePersonale - Opzionale. Il codice personale del cliente. Se `idCliente`
 *   non è fornito, questo codice viene utilizzato per cercare l'ID del cliente tramite una chiamata API.
 * @returns {Promise<void>} Una promise che si risolve quando la navigazione viene attivata
 *   o quando viene soddisfatta una condizione di ritorno anticipato (es. identificatori mancanti, errore API).
 * @throws Mostra una finestra di dialogo di errore utilizzando `errorDialogDefaultFunction` se la chiamata API
 *   per recuperare l'ID cliente tramite `codicePersonale` fallisce.
 *
 * @example
 * // Naviga alla pagina dei dettagli del cliente con ID 123
 * navigateToCliente(navigate, { path: '/clienti/details', idCliente: 123 });
 *
 * @example
 * // Naviga alla pagina dei dettagli del cliente trovato tramite codice personale 'CP001'
 * await navigateToCliente(navigate, { path: '/clienti/details', codicePersonale: 'CP001' });
 *
 * @example
 * // Aggiorna i parametri di ricerca della pagina corrente con l'ID cliente 456
 * navigateToCliente(navigate, { idCliente: 456 });
 *
 * @example
 * // Aggiorna i parametri di ricerca della pagina corrente con l'ID cliente trovato tramite codice personale 'CP002'
 * await navigateToCliente(navigate, { codicePersonale: 'CP002' });
 */
const navigateToCliente = async (
  navigate: NavigateFunction,
  params: {
    path?: string;
    idCliente?: string | number;
    codicePersonale?: string;
  },
) => {
  const { idCliente, codicePersonale, path } = params;
  let navigationId = idCliente;

  if (!codicePersonale && !idCliente) return;

  if (codicePersonale && !idCliente) {
    try {
      const idClientFromCodicePersonale = await axios.get(
        `/focusapi/cliente/clienteFromCodicePersonale/${codicePersonale}`,
      );
      if (idClientFromCodicePersonale) {
        navigationId = idClientFromCodicePersonale.data;
      }
    } catch (e) {
      errorDialogDefaultFunction(
        'Errore durante il caricamento dei dati del cliente.',
      );
      return;
    }
  }

  let searchParams = new URLSearchParams(window.location.search);
  searchParams.set('idCliente', navigationId.toString());
  // Rimuovo idBusta per evitare di navigare con un idBusta stale del cliente precedente
  searchParams.delete('idBusta');

  let navigationParams: {
    search: string;
    pathname?: string;
  } = {
    search: searchParams.toString(),
  };

  if (path) {
    navigationParams.pathname = path;
  }

  navigate(navigationParams);
};

export default navigateToCliente;
